import { getSecureRandomInt } from './secureRandom';
import { estimatePassphraseEntropy } from './passphraseEntropy';
import { tokenizeAndClassify, Locale, type TokenType } from './classifyToken';

/**
 * Types of suggestions for improving passphrase security.
 *
 * These suggestions help users strengthen their own passphrases by:
 * - Adding more entropy through diverse vocabulary
 * - Improving memorability with emojis
 * - Removing weak or predictable elements
 */
export type SuggestionKind = 'ADD_ADJECTIVE' | 'ADD_WORD' | 'ADD_EMOJI' | 'REPLACE_NAME' | 'REMOVE_FILLER';

/**
 * A single suggestion for improving a passphrase.
 *
 * @property kind - The type of improvement being suggested
 * @property message - Localized human-readable explanation of the suggestion
 * @property estimatedDeltaBits - Estimated entropy gain from applying this suggestion
 * @property apply - Function that applies the suggestion to a passphrase
 *
 * @remarks
 * The `apply` function is non-destructive and returns a modified copy.
 * It uses cryptographically secure random selection for any added words/emojis.
 */
export interface Suggestion {
  kind: SuggestionKind;
  message: string;
  estimatedDeltaBits: number;
  apply: (passphrase: string) => string;
}

/**
 * Result of analyzing a passphrase for improvement opportunities.
 *
 * @property bits - Current estimated entropy in bits
 * @property suggestions - List of actionable improvements (max 4)
 */
export interface SuggestionResult {
  bits: number;
  suggestions: Suggestion[];
}

/**
 * Vivid adjectives for English passphrases.
 * These are chosen for being memorable, evocative, and less common.
 */
const VIVID_ADJECTIVES: Record<Locale, string[]> = {
  en: ['crimson', 'azure', 'midnight', 'ember', 'frost', 'shadow', 'silver', 'golden'],
  es: ['carmesí', 'azul', 'nocturno', 'brasa', 'escarcha', 'sombra', 'plateado', 'dorado'],
};

/**
 * Rare nouns for English passphrases.
 * These are chosen for being uncommon, memorable, and diverse.
 */
const RARE_NOUNS: Record<Locale, string[]> = {
  en: ['lynx', 'quartz', 'nebula', 'glacier', 'comet', 'prism', 'zephyr', 'phoenix'],
  es: ['lince', 'cuarzo', 'nebulosa', 'glaciar', 'cometa', 'prisma', 'céfiro', 'fénix'],
};

/**
 * Extra emojis for visual diversity and memorability.
 * These are chosen for being visually distinct and universally recognizable.
 */
const EXTRA_EMOJI: string[] = ['🌙', '⚡', '🌊', '🔥', '🌟', '🎯', '🚀', '💎', '🎨', '🔮'];

/**
 * Messages for different suggestion types, localized by language.
 */
const MESSAGES: Record<Locale, Record<SuggestionKind, string>> = {
  en: {
    ADD_EMOJI: 'Add an emoji to make it more memorable and add ~8 bits of security.',
    ADD_ADJECTIVE: 'Add a vivid adjective before a noun to add ~11 bits of security.',
    ADD_WORD: 'Add another rare word to strengthen your passphrase by ~12 bits.',
    REPLACE_NAME: 'Replace the name with a rare noun to add ~6 bits of security and improve privacy.',
    REMOVE_FILLER: 'Remove filler words (like "and", "the") - they add no security but make it longer.',
  },
  es: {
    ADD_EMOJI: 'Añade un emoji para hacerla más memorable y añadir ~8 bits de seguridad.',
    ADD_ADJECTIVE: 'Añade un adjetivo vívido antes de un sustantivo para añadir ~11 bits de seguridad.',
    ADD_WORD: 'Añade otra palabra rara para fortalecer tu frase por ~12 bits.',
    REPLACE_NAME: 'Reemplaza el nombre con un sustantivo raro para añadir ~6 bits de seguridad y mejorar privacidad.',
    REMOVE_FILLER: 'Elimina palabras de relleno (como "y", "el") - no añaden seguridad pero la hacen más larga.',
  },
};

/**
 * Analyze a passphrase and suggest improvements to increase security.
 *
 * This function provides actionable, specific suggestions to help users strengthen
 * their own passphrases. It does NOT generate pre-made passphrases - instead, it
 * analyzes the user's input and offers targeted improvements.
 *
 * @param passphrase - The user's current passphrase to analyze
 * @param locale - Language for localized messages ('en' or 'es')
 * @returns Analysis result with current entropy and up to 4 suggestions
 *
 * @remarks
 * Suggestions are prioritized by:
 * 1. Security impact (entropy gain)
 * 2. Ease of implementation
 * 3. Memorability improvement
 *
 * Each suggestion's `apply` function uses cryptographically secure random
 * selection when choosing words or emojis to add.
 *
 * @example
 * ```typescript
 * const result = suggestImprovements('myDogFluffy2023', 'en');
 * console.log(`Current: ${result.bits} bits`);
 * result.suggestions.forEach(s => {
 *   console.log(s.message);
 *   console.log('New:', s.apply('myDogFluffy2023'));
 * });
 * ```
 */
export function suggestImprovements(passphrase: string, locale: Locale): SuggestionResult {
  const { bits, tokens } = estimatePassphraseEntropy(passphrase, locale);
  const suggestions: Suggestion[] = [];

  // Count existing emojis
  const emojiCount = tokens.filter(t => t.type === 'EMOJI').length;

  // Find word tokens (potential nouns)
  const wordTokens = tokens.filter(t => t.type === 'WORD');
  const firstWordIndex = tokens.findIndex(t => t.type === 'WORD');

  // Find name-like tokens (FirstCaps pattern)
  const nameTokenIndex = tokens.findIndex(t =>
    t.type === 'WORD' &&
    t.value.length > 2 &&
    /^[A-Z][a-z]+$/.test(t.value)
  );

  // Check for filler words
  const hasFiller = tokens.some(t => t.type === 'FILLER');

  // Suggestion 1: Add emoji if none exist
  if (emojiCount === 0) {
    suggestions.push({
      kind: 'ADD_EMOJI',
      message: MESSAGES[locale].ADD_EMOJI,
      estimatedDeltaBits: 8,
      apply: (phrase: string): string => {
        const randomIndex = getSecureRandomInt(0, EXTRA_EMOJI.length - 1);
        return phrase + EXTRA_EMOJI[randomIndex];
      },
    });
  }

  // Suggestion 2: Add another emoji if weak and < 2 emojis
  if (emojiCount < 2 && bits < 60 && suggestions.length < 4) {
    suggestions.push({
      kind: 'ADD_EMOJI',
      message: MESSAGES[locale].ADD_EMOJI,
      estimatedDeltaBits: 8,
      apply: (phrase: string): string => {
        const randomIndex = getSecureRandomInt(0, EXTRA_EMOJI.length - 1);
        return phrase + EXTRA_EMOJI[randomIndex];
      },
    });
  }

  // Suggestion 3: Add adjective before first noun
  if (firstWordIndex >= 0 && wordTokens.length > 0 && suggestions.length < 4) {
    suggestions.push({
      kind: 'ADD_ADJECTIVE',
      message: MESSAGES[locale].ADD_ADJECTIVE,
      estimatedDeltaBits: 11,
      apply: (phrase: string): string => {
        const parts = tokenizeAndClassify(phrase, locale);
        const randomIndex = getSecureRandomInt(0, VIVID_ADJECTIVES[locale].length - 1);
        const adjective = VIVID_ADJECTIVES[locale][randomIndex];

        // Insert adjective before first WORD token
        const result: string[] = [];
        let inserted = false;

        for (const part of parts) {
          if (!inserted && part.type === 'WORD') {
            result.push(adjective);
            result.push(' ');
            inserted = true;
          }
          result.push(part.value);
        }

        return result.join('');
      },
    });
  }

  // Suggestion 4: Add word if still weak
  if (bits < 70 && suggestions.length < 4) {
    suggestions.push({
      kind: 'ADD_WORD',
      message: MESSAGES[locale].ADD_WORD,
      estimatedDeltaBits: 12,
      apply: (phrase: string): string => {
        const randomIndex = getSecureRandomInt(0, RARE_NOUNS[locale].length - 1);
        const word = RARE_NOUNS[locale][randomIndex];

        // Add separator if phrase doesn't end with space or special char
        const separator = /[\s\-_]$/.test(phrase) ? '' : ' ';
        return phrase + separator + word;
      },
    });
  }

  // Suggestion 5: Replace name with rare noun
  if (nameTokenIndex >= 0 && suggestions.length < 4) {
    suggestions.push({
      kind: 'REPLACE_NAME',
      message: MESSAGES[locale].REPLACE_NAME,
      estimatedDeltaBits: 6,
      apply: (phrase: string): string => {
        const parts = tokenizeAndClassify(phrase, locale);
        const randomIndex = getSecureRandomInt(0, RARE_NOUNS[locale].length - 1);
        const replacement = RARE_NOUNS[locale][randomIndex];

        const result: string[] = [];
        let replaced = false;

        for (const part of parts) {
          if (!replaced && part.type === 'WORD' && /^[A-Z][a-z]+$/.test(part.value)) {
            result.push(replacement);
            replaced = true;
          } else {
            result.push(part.value);
          }
        }

        return result.join('');
      },
    });
  }

  // Suggestion 6: Remove filler words
  if (hasFiller && suggestions.length < 4) {
    suggestions.push({
      kind: 'REMOVE_FILLER',
      message: MESSAGES[locale].REMOVE_FILLER,
      estimatedDeltaBits: 0,
      apply: (phrase: string): string => {
        const parts = tokenizeAndClassify(phrase, locale);

        // Filter out FILLER tokens and collapse multiple spaces
        const result: string[] = [];
        let lastWasSpace = false;

        for (const part of parts) {
          if (part.type === 'FILLER') {
            // Skip filler words, but preserve spacing
            if (!lastWasSpace && result.length > 0) {
              result.push(' ');
              lastWasSpace = true;
            }
          } else {
            const isSpace = part.value === ' ';
            if (isSpace) {
              if (!lastWasSpace) {
                result.push(part.value);
                lastWasSpace = true;
              }
            } else {
              result.push(part.value);
              lastWasSpace = false;
            }
          }
        }

        return result.join('').trim();
      },
    });
  }

  // Return top 4 suggestions
  return {
    bits,
    suggestions: suggestions.slice(0, 4),
  };
}
